import { useCallback } from 'react';

import { useQueryParams } from './useQueryParams';
import { stripQueryParameters } from '../hash/hashUtils';
import { unsafeNavigate } from '../navigate';
import { getCurrentUrlWithoutHash } from '../useCurrentRoute';
import type { QueryParameterValue, QueryParameters } from '../types/NavigateOptions';

type SearchParamValue = QueryParameterValue;
type SearchParamReturnValue = string | undefined;

/**
 * Imperative API to set the query parameters in the current URL path (after the hash).
 *
 * Unlike the {@link useManageQueryParameters} hook, this function does NOT merge with the
 * existing query string — it replaces it entirely with `params`. Any current query parameter
 * that is not included in `params` will be dropped. To preserve other parameters, read them
 * first and spread them into `params` yourself (e.g. `{ ...currentParams, ...updates }`).
 * Passing an empty object (`{}`) therefore clears all query parameters.
 *
 * Prefer the {@link useManageQueryParameters} or `useQueryParam` hooks inside React components
 * (they merge automatically and are safer). Use this function only in non-hook contexts (e.g.
 * plain event handlers or helpers) or when a full query-string replacement is intended.
 *
 * @param {QueryParameters} params - The complete set of query parameters to set on the URL
 * @param {Object} [opts] - Options for the update operation
 * @param {boolean} [opts.replace] - If true, replaces the current history entry instead of adding a new one
 *
 */
export function updatePathQueryParams(params: QueryParameters, opts?: { replace: boolean }): void {
    unsafeNavigate(stripQueryParameters(getCurrentUrlWithoutHash()), {
        replace: opts?.replace, // replace url
        queryParameters: params,
    });
}

const updateOrRemoveParam = (
    params: QueryParameters,
    name: string,
    value: SearchParamValue,
): void => {
    if (value === undefined || value === null || value === '') {
        delete params[name];
        return;
    }

    params[name] = Array.isArray(value) ? value : value.toString();
};

type UseManageQueryParametersReturn<QueryParameter extends string, SearchParamsValueType> = {
    updateParam: (name: QueryParameter, value: SearchParamValue, replace?: boolean) => void;
    updateAllParams: (
        params: Partial<Record<QueryParameter, SearchParamValue>>,
        replace?: boolean,
    ) => void;
    getParam: (name: QueryParameter) => SearchParamReturnValue;
    removeParam: (name: QueryParameter) => void;
    searchParams: Record<string, SearchParamsValueType>;
};

export function useManageQueryParameters<QueryParameter extends string>(
    parseArrays?: boolean,
): UseManageQueryParametersReturn<QueryParameter, string>;
export function useManageQueryParameters<QueryParameter extends string>(
    parseArrays: true,
): UseManageQueryParametersReturn<QueryParameter, string | string[]>;

/**
 * A hook for managing query parameters in the URL.
 *
 * @template QueryParameter - The type of the query parameter names (should extend string)
 * @param {boolean} [parseArrays] - If true, parses array-like query parameters into arrays
 * @returns {UseManageQueryParametersReturn<QueryParameter, string | string[]>} An object with methods to manage query parameters
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { updateParam, getParam, removeParam } = useManageQueryParameters<'page' | 'filter'>();
 *
 * // Update a single parameter
 * updateParam('page', '2');
 *
 * // Get a parameter value
 * const currentPage = getParam('page');
 *
 * // Remove a parameter
 * removeParam('filter');
 *
 * // Usage with array parsing
 * const { updateAllParams, searchParams } = useManageQueryParameters<'tags'>(true);
 *
 * // Update multiple parameters, including an array
 * updateAllParams({ tags: ['react', 'typescript'] });
 *
 * // Access parsed array parameters
 * console.log(searchParams.tags); // ['react', 'typescript']
 * ```
 */
export function useManageQueryParameters<QueryParameter extends string>(
    parseArrays?: boolean,
): UseManageQueryParametersReturn<QueryParameter, string | string[]> {
    const searchParams = useQueryParams({ parseArrays });
    const searchParamsForUpdate = useQueryParams({ parseArrays: true });

    const updateParam = useCallback(
        (name: QueryParameter, value: SearchParamValue, replace?: boolean): void => {
            const searchParamsCopy = { ...searchParamsForUpdate };
            updateOrRemoveParam(searchParamsCopy, name, value);
            updatePathQueryParams(searchParamsCopy, replace ? { replace } : undefined);
        },
        [searchParamsForUpdate],
    );

    const removeParam = useCallback(
        (name: QueryParameter): void => {
            updateParam(name, null);
        },
        [updateParam],
    );

    const updateAllParams = useCallback(
        (params: Partial<Record<QueryParameter, SearchParamValue>>, replace?: boolean): void => {
            const paramEntries = Object.entries<SearchParamValue>(params) as [
                QueryParameter,
                SearchParamValue,
            ][];
            const searchParamsCopy = { ...searchParamsForUpdate };

            paramEntries.forEach(([paramName, paramValue]) =>
                updateOrRemoveParam(searchParamsCopy, paramName, paramValue),
            );

            updatePathQueryParams(searchParamsCopy, replace ? { replace } : undefined);
        },
        [searchParamsForUpdate],
    );

    const getParam = useCallback(
        (name: QueryParameter): SearchParamReturnValue => {
            return searchParams[name] || undefined;
        },
        [searchParams],
    );

    return {
        updateParam,
        updateAllParams,
        getParam,
        removeParam,
        searchParams,
    };
}
